import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { VisitContextRefSchema, VisitContextSectionsSchema } from "./visit-context.js";
export const ClinicalSourceSchema = z.object({
    sourceId: IdSchema,
    sourceType: z.enum([
        "note",
        "problem",
        "allergy",
        "medication",
        "observation",
        "diagnostic_report",
        "document",
    ]),
    version: z.number().int().positive(),
    label: z.string().min(1).max(160),
    recordedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema.optional(),
});
export const AllergySummarySchema = z.object({
    id: IdSchema,
    substanceCode: z.string().min(1).max(80),
    display: z.string().min(1).max(160),
    severity: z.enum(["mild", "moderate", "severe", "unknown"]),
});
export const MedicationSummarySchema = z.object({
    id: IdSchema,
    medicationCode: z.string().min(1).max(80),
    display: z.string().min(1).max(160),
    status: z.enum(["active", "inactive", "unknown"]),
});
/**
 * The pre-warmed Patient Visit Context attached to a governed run: the exact
 * assembled version (ref) plus its sections, composed once at run creation so
 * the context manifest and the model input describe the same bytes.
 */
export const EncounterVisitContextSchema = z.object({
    contextRef: VisitContextRefSchema,
    assembledAt: IsoDateTimeSchema,
    tier: z.enum(["full", "core"]),
    sections: VisitContextSectionsSchema,
});
export const EncounterContextSchema = z.object({
    tenantId: IdSchema,
    siteId: IdSchema,
    encounterId: IdSchema,
    encounterVersion: z.number().int().positive(),
    patientId: IdSchema,
    patientDisplayName: z.string().min(1).max(160),
    patientLegalName: z.string().min(1).max(160).optional(),
    /**
     * The legal name's parts as recorded, middle name included. Letters print
     * `patientLegalName`; these let the model boundary recognise the full name in
     * other orders and cases.
     */
    legalFirstName: z.string().min(1).max(160).optional(),
    legalMiddleName: z.string().min(1).max(160).optional(),
    legalLastName: z.string().min(1).max(160).optional(),
    dateOfBirth: z.string().date(),
    sexAtBirth: z.enum(["female", "male", "intersex", "unknown"]),
    noteText: z.string().max(50_000),
    allergies: z.array(AllergySummarySchema),
    medications: z.array(MedicationSummarySchema),
    problemCodes: z.array(z.string().min(1).max(80)),
    sources: z.array(ClinicalSourceSchema),
    activeOrderFingerprints: z.array(z.string()),
    missingRequiredFacts: z.array(z.string().min(1).max(160)),
    /** Present when the run composed a warmed Visit Context; absent otherwise (additive). */
    visitContext: EncounterVisitContextSchema.optional(),
    /**
     * The current note's SOAP sections when the note is structured. The referral
     * letter's "relevant history" is built from these instead of the flattened
     * note text (REF-2).
     */
    noteSections: z
        .object({
        subjective: z.string().max(50_000).optional(),
        objective: z.string().max(50_000).optional(),
        assessment: z.string().max(50_000).optional(),
        plan: z.string().max(50_000).optional(),
    })
        .strict()
        .optional(),
});
//# sourceMappingURL=clinical.js.map